import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, ChevronUp, Settings, Save, X, Instagram, Send } from 'lucide-react';
import { contentApi, knowledgeApi, KnowledgeItem } from '../services/api';
import { KnowledgeSelector } from './KnowledgeSelector';
import { useContentPlanCache } from '../hooks/useContentPlanCache';


// Функция для извлечения ника из ссылки
const extractUsernameFromLink = (input: string): string => {
  if (!input) return '';
  
  // Убираем пробелы
  let cleaned = input.trim();
  
  // Если это обычный ник без ссылки (нет протокола и слешей)
  if (!/^https?:\/\//.test(cleaned) && !cleaned.includes('/')) {
    // Убираем @ если есть, но оставляем остальные символы как есть
    // Пользователь может вводить любой текст
    return cleaned.replace(/^@/, '');
  }
  
  try {
    // Создаем URL объект для парсинга
    const url = new URL(cleaned);
    
    // Извлекаем путь и убираем слеши
    let pathname = url.pathname.replace(/^\/+|\/+$/g, '');
    
    // Для Instagram и Telegram берем первую часть пути
    const parts = pathname.split('/');
    const username = parts[0];
    
    // Убираем @ если есть и возвращаем только для валидных ссылок
    return username.replace(/^@/, '').replace(/[^a-zA-Z0-9_\.]/g, '');
  } catch (e) {
    // Если не удалось распарсить как URL, возвращаем как есть (убираем только @)
    // Не фильтруем символы, чтобы пользователь мог свободно вводить текст
    return cleaned.replace(/^@/, '');
  }
};

// Интерфейсы для настроек контент-плана (соответствуют backend моделям)
interface SocialNetworksSettings {
  instagram: boolean;
  telegram: boolean;
}

interface InstagramFormatsSettings {
  reels: boolean;
  carousel: boolean;
  post: boolean;
}

interface FrequencySettings {
  reels: number;
  carousel: number;
  post: number;
  telegram: number;
}


interface ContentPlanSettings {
  topic: string;
  description: string;
  services: string;
  aboutAuthor: string;
  contentPreferences: string;
  audienceArticleId: string | null;
  authorSocialHandle: string;
  referenceSocialHandle: string;
  profileAnalyse?: string;
  socialNetworks: SocialNetworksSettings;
  instagramFormats: InstagramFormatsSettings;
  reelsComplexity: 'simple' | 'medium' | 'complex';
  frequency: FrequencySettings;
  duration: number;
}

interface ContentPlanSettingsProps {
  contentPlanId: string;
  isVisible: boolean;
  onToggle: () => void;
  initialSettings: ContentPlanSettings | null;
}

export const ContentPlanSettings: React.FC<ContentPlanSettingsProps> = ({
  contentPlanId,
  isVisible,
  onToggle,
  initialSettings,
}) => {
  const [settings, setSettings] = useState<ContentPlanSettings | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<ContentPlanSettings | null>(null);
  
  // Хук для инвалидации кэша
  const { invalidateContentPlanData } = useContentPlanCache();
  
  // Состояния для управления уведомлениями о преобразовании ссылок
  const [showLinkConversionMessage, setShowLinkConversionMessage] = useState(false);
  const [linkConversionTimeout, setLinkConversionTimeout] = useState<NodeJS.Timeout | null>(null);

  // Состояния для управления вводом социальных сетей
  const [isEditingSocialHandle, setIsEditingSocialHandle] = useState(false);
  const [isEditingCompetitorHandle, setIsEditingCompetitorHandle] = useState(false);
  const socialHandleInputRef = useRef<HTMLInputElement>(null);
  const competitorHandleInputRef = useRef<HTMLInputElement>(null);

  // Состояние для списка документов из базы знаний
  const [articles, setArticles] = useState<KnowledgeItem[]>([]);
  
  // Константные значения для типов файлов
  const audienceFileTypes = useMemo(() => ['audience'], []);

  // Сброс состояния при смене contentPlanId
  useEffect(() => {
    setSettings(null);
    setOriginalSettings(null);
    setHasChanges(false);
  }, [contentPlanId]);

  // Инициализация настроек из пропсов
  useEffect(() => {
    // Этот эффект должен срабатывать только тогда, когда initialSettings действительно меняются
    // и нет несохраненных изменений.
    if (initialSettings) {
      const newSettings = JSON.parse(JSON.stringify(initialSettings));
      if (!hasChanges) {
        setSettings(newSettings);
        setOriginalSettings(newSettings);
      }
    }
  }, [initialSettings]);

  // Валидация документов после загрузки articles
  useEffect(() => {
    if (settings && articles.length > 0) {
      const findDocumentById = (documentId: string | null): boolean => {
        if (!documentId) return false;
        
        const findInItems = (items: KnowledgeItem[]): boolean => {
          for (const item of items) {
            if (item.id === documentId) return true;
            if (item.children && findInItems(item.children)) return true;
          }
          return false;
        };
        
        return findInItems(articles);
      };

      let needsUpdate = false;
      const updatedSettings = { ...settings };

      // Проверяем audienceArticleId
      if (settings.audienceArticleId && !findDocumentById(settings.audienceArticleId)) {
        updatedSettings.audienceArticleId = null;
        needsUpdate = true;
      }


      if (needsUpdate) {
        setSettings(updatedSettings);
        setHasChanges(true);
      }
    }
  }, [settings, articles]);

  // Загрузка документов из базы знаний при монтировании
  useEffect(() => {
    const loadItems = async () => {
      try {
        const items = await knowledgeApi.getItems();
        setArticles(items);
      } catch (error) {
        console.error('Error loading articles:', error);
      }
    };
    loadItems();
  }, []);

  // Функция для автоматического изменения высоты textarea
  const autoResizeTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 160); // Ограничиваем максимальной высотой 160px
    textarea.style.height = `${newHeight}px`;
    
          // Показываем или скрываем скролл в зависимости от превышения максимальной высоты
      if (textarea.scrollHeight > 160) {
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.overflowY = 'hidden';
      }
  };

  // Функция для автоматического изменения размера всех textarea на странице
  const autoResizeAllTextareas = () => {
    const textareas = document.querySelectorAll('.auto-resize-textarea');
    textareas.forEach(textarea => {
      const el = textarea as HTMLTextAreaElement;
      el.style.height = 'auto';
      const newHeight = Math.min(el.scrollHeight, 160); // Ограничиваем максимальной высотой 160px
      el.style.height = `${newHeight}px`;
      
      // Показываем или скрываем скролл в зависимости от превышения максимальной высоты
      if (el.scrollHeight > 160) {
        el.style.overflowY = 'auto';
      } else {
        el.style.overflowY = 'hidden';
      }
    });
  };

  // Автоматическое изменение размера textarea при изменении настроек или видимости
  useEffect(() => {
    if (isVisible && settings) {
      // Небольшая задержка для корректной работы анимации раскрытия
      setTimeout(() => {
        autoResizeAllTextareas();
      }, 350); // Чуть больше времени анимации (300ms)
    }
  }, [isVisible, settings]);

  // Очистка таймера при размонтировании компонента
  useEffect(() => {
    return () => {
      if (linkConversionTimeout) {
        clearTimeout(linkConversionTimeout);
      }
    };
  }, [linkConversionTimeout]);

  // Эффекты для автоматического фокуса на поля ввода при редактировании
  useEffect(() => {
    if (isEditingSocialHandle && socialHandleInputRef.current) {
      socialHandleInputRef.current.focus();
    }
  }, [isEditingSocialHandle]);

  useEffect(() => {
    if (isEditingCompetitorHandle && competitorHandleInputRef.current) {
      competitorHandleInputRef.current.focus();
    }
  }, [isEditingCompetitorHandle]);

  // Устанавливаем режим редактирования если поля пустые только при инициализации
  useEffect(() => {
    if (initialSettings && !initialSettings.authorSocialHandle) {
      setIsEditingSocialHandle(true);
    }
    if (initialSettings && !initialSettings.referenceSocialHandle) {
      setIsEditingCompetitorHandle(true);
    }
  }, [initialSettings]);


  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    
    // Автоматическое изменение высоты для textarea
    if (e.target instanceof HTMLTextAreaElement) {
      autoResizeTextarea(e as React.ChangeEvent<HTMLTextAreaElement>);
    }
    
    // Для полей социальных сетей фильтруем русские буквы и недопустимые символы
    if (name === 'authorSocialHandle' || name === 'referenceSocialHandle') {
      // Если это URL Instagram, не фильтруем
      if (value.includes('instagram.com')) {
        setSettings(prev => prev ? {
          ...prev,
          [name]: value
        } : null);
      } else {
        // Фильтруем только латинские буквы, цифры, точки, подчеркивания и @
        const filteredValue = value.replace(/[^a-zA-Z0-9._@]/g, '');
        setSettings(prev => prev ? {
          ...prev,
          [name]: filteredValue
        } : null);
      }
    } else {
      // Для остальных полей сохраняем значение как есть
      setSettings(prev => prev ? {
        ...prev,
        [name]: value
      } : null);
    }
    setHasChanges(true);
  };

  // Отдельный обработчик для потери фокуса полей социальных сетей
  const handleSocialHandleBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    
    if (name === 'authorSocialHandle' || name === 'referenceSocialHandle') {
      // Извлекаем ник из ссылки или очищаем от недопустимых символов
      const extractedUsername = extractUsernameFromLink(value);
      
      // Проверяем, было ли что-то изменено (ссылка преобразована или символы отфильтрованы)
      if (value !== extractedUsername && value.trim() !== '') {
        // Отменяем предыдущий таймер, если он есть
        if (linkConversionTimeout) {
          clearTimeout(linkConversionTimeout);
        }
        
        // Показываем сообщение о преобразовании
        setShowLinkConversionMessage(true);
        
        // Устанавливаем таймер для скрытия сообщения
        const timeoutId = setTimeout(() => {
          setShowLinkConversionMessage(false);
        }, 3000);
        
        setLinkConversionTimeout(timeoutId);
      }
      
      // Устанавливаем извлеченный/очищенный ник только если значение изменилось
      if (value !== extractedUsername) {
        setSettings(prev => prev ? {
          ...prev,
          [name]: extractedUsername
        } : null);
        setHasChanges(true);
      }
    }
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    if (!settings) return;
    
    const updatedSettings = { ...settings };
    
    if (field.includes('.')) {
      const [parentField, childField] = field.split('.');
      const parentValue = updatedSettings[parentField as keyof ContentPlanSettings];
      if (typeof parentValue === 'object' && parentValue !== null) {
        (updatedSettings as any)[parentField] = {
          ...parentValue,
          [childField]: checked
        };
      }
    } else {
      (updatedSettings as any)[field] = checked;
    }
    
    setSettings(updatedSettings);
    setHasChanges(true);
  };

  const handleFrequencyChange = (type: string, value: number) => {
    if (!settings) return;
    
    setSettings(prev => prev ? {
      ...prev,
      frequency: {
        ...prev.frequency,
        [type]: value
      }
    } : null);
    setHasChanges(true);
  };

  const handleComplexityChange = (complexity: string) => {
    if (!settings) return;
    
    setSettings(prev => prev ? {
      ...prev,
      reelsComplexity: complexity as 'simple' | 'medium' | 'complex'
    } : null);
    setHasChanges(true);
  };

  const handleDeleteProfile = () => {
    if (!settings) return;
    
    setSettings(prev => prev ? {
      ...prev,
      authorSocialHandle: ''
    } : null);
    setIsEditingSocialHandle(true);
    setHasChanges(true);
  };

  const handleDeleteCompetitorProfile = () => {
    if (!settings) return;
    
    setSettings(prev => prev ? {
      ...prev,
      referenceSocialHandle: ''
    } : null);
    setIsEditingCompetitorHandle(true);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!settings || !hasChanges) return;
    
    console.log('💾 [SETTINGS] Начинаем сохранение настроек для contentPlanId:', contentPlanId);
    setIsSaving(true);
    try {
      await contentApi.updateContentPlanSettings({
        contentPlanId,
        settings
      });
      
      console.log('✅ [SETTINGS] Настройки успешно сохранены, вызываем инвалидацию кэша');
      // Инвалидируем кэш после успешного сохранения
      invalidateContentPlanData(contentPlanId);
      
      const savedSettings = JSON.parse(JSON.stringify(settings));
      setOriginalSettings(savedSettings);
      setSettings(savedSettings); // Синхронизируем settings с сохраненными данными
      setHasChanges(false);
      console.log('✅ [SETTINGS] Состояние обновлено, hasChanges = false');
    } catch (error) {
      console.error('❌ [SETTINGS] Ошибка сохранения настроек:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalSettings) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
      setHasChanges(false);
    }
  };

  if (!settings && !initialSettings) {
    return null;
  }

  const isInstagram = settings?.socialNetworks?.instagram;
  const isTelegram = settings?.socialNetworks?.telegram;

  // Обработчики для выбора документов
  const handleAudienceArticleSelect = (item: KnowledgeItem | null) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      audienceArticleId: item?.id || null
    });
    setHasChanges(true);
  };
  

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 mb-6">
      {/* Заголовок с кнопкой раскрытия */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-t-xl"
      >
        <div className="flex items-center space-x-3">
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Настройки контент-плана
          </h3>
          {hasChanges && (
            <span className="w-2 h-2 bg-orange-500 rounded-full" title="Есть несохраненные изменения" />
          )}
        </div>
        {isVisible ? (
          <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        )}
      </button>

      {/* Содержимое спойлера */}
      {isVisible && (
        <div className="transition-opacity duration-300 ease-in-out opacity-100">
        <div className="px-6 pb-12">
          {settings ? (
            <div className="space-y-6">
              {/* Основная информация */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Тематика блога
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Укажите конкретную нишу вашего блога. Чем точнее тема, тем качественнее будет контент-план.
                  </p>
                  <textarea
                    name="topic"
                    value={settings.topic || ''}
                    onChange={handleInputChange}
                    className="auto-resize-textarea w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white resize-none overflow-hidden"
                    placeholder="Например: Маркетинг для малого бизнеса"
                    rows={1}
                    style={{minHeight: '40px', maxHeight: '160px'}}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Описание цели блога
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Опишите бизнес-цели, которые планируете достичь с помощью контента.
                  </p>
                  <textarea
                    name="description"
                    value={settings.description || ''}
                    onChange={handleInputChange}
                    className="auto-resize-textarea w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white resize-none overflow-hidden"
                    placeholder="Опишите цели вашего контент-плана..."
                    rows={1}
                    style={{minHeight: '40px', maxHeight: '160px'}}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Опишите ваши услуги, продукт, если есть
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Перечислите продукты или услуги, которые хотите продвигать через контент.
                  </p>
                  <textarea
                    name="services"
                    value={settings.services || ''}
                    onChange={handleInputChange}
                    className="auto-resize-textarea w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white resize-none overflow-hidden"
                    placeholder="Расскажите о ваших услугах или продуктах..."
                    rows={1}
                    style={{minHeight: '40px', maxHeight: '160px'}}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Опишите себя
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Расскажите о вашей экспертизе, опыте и ценностях бренда.
                  </p>
                  <textarea
                    name="aboutAuthor"
                    value={settings.aboutAuthor || ''}
                    onChange={handleInputChange}
                    className="auto-resize-textarea w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white resize-none overflow-hidden"
                    placeholder="Расскажите немного о себе или вашем бренде..."
                    rows={1}
                    style={{minHeight: '40px', maxHeight: '160px'}}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Предпочтения в контенте
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Укажите ваши предпочтения по локациям съемки, типам контента, стилю подачи и другим особенностям.
                  </p>
                  <textarea
                    name="contentPreferences"
                    value={settings.contentPreferences || ''}
                    onChange={handleInputChange}
                    className="auto-resize-textarea w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-900 dark:text-white resize-none overflow-hidden"
                    placeholder="Например: предпочитаю съемку в студии, минималистичный стиль, избегаю слишком яркие цвета..."
                    rows={1}
                    style={{minHeight: '40px', maxHeight: '160px'}}
                  />
                </div>

                {/* Целевая аудитория */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Целевая аудитория
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    Выберите документ с анализом целевой аудитории для адаптации контента под интересы подписчиков.
                  </p>
                  <div className=""> 
                    <KnowledgeSelector
                      onSelect={handleAudienceArticleSelect}
                      placeholder="Выберите документ с целевой аудиторией"
                      fileTypes={audienceFileTypes}
                      selectedItemId={settings.audienceArticleId}
                      preloadedItems={articles}
                    />
                  </div>
                </div>
                

                {/* Поля для ввода двух аккаунтов */}
                <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
                  {/* Основной аккаунт */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {isInstagram ? 'Ваш аккаунт Instagram' : 'Ваш канал Telegram'}
                    </label>
                    <div className="relative">
                      {showLinkConversionMessage && (
                        <div className="absolute -top-14 sm:-top-10 left-0 right-0 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 p-2 rounded-md text-xs border border-blue-200 dark:border-blue-800 shadow-sm z-10 text-center">
                          Ссылка преобразована в ник. Можно вставлять ссылки или вводить ник напрямую
                        </div>
                      )}
                      
                      <div className="flex items-center gap-3">
                        {isEditingSocialHandle ? (
                          <input
                            type="text"
                            name="authorSocialHandle"
                            value={settings.authorSocialHandle || ''}
                            onChange={handleInputChange}
                            onBlur={(e) => {
                              handleSocialHandleBlur(e);
                              // Выходим из режима редактирования, только если после обработки поле не пустое
                              const finalValue = extractUsernameFromLink(e.target.value);
                              if (finalValue.trim()) {
                                setIsEditingSocialHandle(false);
                              }
                            }}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder={isInstagram ? 'ник или ссылка: instagram.com/ваш_профиль' : 'имя или ссылка: t.me/ваш_канал'}
                            ref={socialHandleInputRef}
                          />
                        ) : settings.authorSocialHandle ? (
                          <div className="w-full flex items-center gap-2">
                            <div 
                              onClick={() => setIsEditingSocialHandle(true)}
                              className="flex-1 inline-flex items-center px-3 py-2 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md text-sm cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors"
                            >
                              {isInstagram ? (
                                <Instagram className="w-4 h-4 mr-2" />
                              ) : (
                                <Send className="w-4 h-4 mr-2" />
                              )}
                              @{settings.authorSocialHandle}
                            </div>
                            <button
                              onClick={handleDeleteProfile}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/40 transition-colors"
                              title="Удалить профиль"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setIsEditingSocialHandle(true)}
                            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center justify-center"
                          >
                            {isInstagram ? (
                              <Instagram className="w-4 h-4 mr-2" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            {isInstagram ? 'Добавить профиль Instagram' : 'Добавить канал Telegram'}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-2 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 flex items-start">
                      <div className="mr-2 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="20" x2="18" y2="10"></line>
                          <line x1="12" y1="20" x2="12" y2="4"></line>
                          <line x1="6" y1="20" x2="6" y2="14"></line>
                          <line x1="3" y1="20" x2="21" y2="20"></line>
                        </svg>
                      </div>
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="font-medium">Анализ вашего профиля:</span> изучим стиль подачи контента и тематику для создания адаптированного под ваш бренд контент-плана.
                      </p>
                    </div>
                  </div>

                  {/* Аккаунт конкурента */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {isInstagram ? 'Аккаунт конкурента Instagram' : 'Канал конкурента Telegram (необязательно)'}
                    </label>
                    <div className="flex items-center gap-3">
                      {isEditingCompetitorHandle ? (
                        <input
                          type="text"
                          name="referenceSocialHandle"
                          value={settings.referenceSocialHandle || ''}
                          onChange={handleInputChange}
                          onBlur={(e) => {
                            handleSocialHandleBlur(e);
                            // Выходим из режима редактирования, только если после обработки поле не пустое
                            const finalValue = extractUsernameFromLink(e.target.value);
                            if (finalValue.trim()) {
                              setIsEditingCompetitorHandle(false);
                            }
                          }}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                          placeholder={isInstagram ? 'ник или ссылка: instagram.com/конкурент' : 'имя или ссылка: t.me/конкурент'}
                          ref={competitorHandleInputRef}
                        />
                      ) : settings.referenceSocialHandle ? (
                        <div className="w-full flex items-center gap-2">
                          <div 
                            onClick={() => setIsEditingCompetitorHandle(true)}
                            className="flex-1 inline-flex items-center px-3 py-2 bg-orange-100 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-md text-sm cursor-pointer hover:bg-orange-200 dark:hover:bg-orange-800/40 transition-colors"
                          >
                            {isInstagram ? (
                              <Instagram className="w-4 h-4 mr-2" />
                            ) : (
                              <Send className="w-4 h-4 mr-2" />
                            )}
                            @{settings.referenceSocialHandle}
                          </div>
                          <button
                            onClick={handleDeleteCompetitorProfile}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-800/40 transition-colors"
                            title="Удалить профиль конкурента"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setIsEditingCompetitorHandle(true)}
                          className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 hover:border-orange-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors flex items-center justify-center"
                        >
                          {isInstagram ? (
                            <Instagram className="w-4 h-4 mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {isInstagram ? 'Добавить конкурента (необязательно)' : 'Добавить конкурента Telegram (необязательно)'}
                        </button>
                      )}
                    </div>
                    
                    <div className="mt-2 bg-orange-50 dark:bg-orange-900/10 rounded-lg p-3 flex items-start">
                      <div className="mr-2 text-orange-500 dark:text-orange-400 flex-shrink-0 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3"></circle>
                          <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"></path>
                        </svg>
                      </div>
                      <p className="text-xs text-orange-700 dark:text-orange-300">
                        <span className="font-medium">Анализ референс-аккаунта:</span> изучим стратегию и стиль подачи контента для выявления эффективных подходов и используем при создании контент-плана.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Форматы Instagram */}
              {isInstagram && (
                <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                    Форматы для Instagram
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div 
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        settings?.instagramFormats?.reels 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500 dark:text-gray-300' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:text-gray-300'
                      }`}
                      onClick={() => handleCheckboxChange('instagramFormats.reels', !settings?.instagramFormats?.reels)}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                        settings?.instagramFormats?.reels
                          ? 'bg-blue-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        {settings?.instagramFormats?.reels && (
                          <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium cursor-pointer select-none">Reels</span>
                    </div>

                    <div 
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        settings?.instagramFormats?.carousel 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500 dark:text-gray-300' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:text-gray-300'
                      }`}
                      onClick={() => handleCheckboxChange('instagramFormats.carousel', !settings?.instagramFormats?.carousel)}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                        settings?.instagramFormats?.carousel
                          ? 'bg-blue-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        {settings?.instagramFormats?.carousel && (
                          <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium cursor-pointer select-none">Карусель</span>
                    </div>

                    <div 
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        settings?.instagramFormats?.post 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500 dark:text-gray-300' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:text-gray-300'
                      }`}
                      onClick={() => handleCheckboxChange('instagramFormats.post', !settings?.instagramFormats?.post)}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                        settings?.instagramFormats?.post
                          ? 'bg-blue-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}>
                        {settings?.instagramFormats?.post && (
                          <svg className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="font-medium cursor-pointer select-none">Посты</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Сложность Reels */}
              {isInstagram && settings?.instagramFormats?.reels && (
                <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">
                    Уровень сложности сценариев Reels
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div 
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        settings.reelsComplexity === 'simple' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => handleComplexityChange('simple')}
                    >
                      <span className={`font-medium text-center ${
                        settings.reelsComplexity === 'simple'
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>Простой</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Базовые сценарии, легкие в реализации
                      </p>
                    </div>
                    
                    <div 
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        settings.reelsComplexity === 'medium' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => handleComplexityChange('medium')}
                    >
                      <span className={`font-medium text-center ${
                        settings.reelsComplexity === 'medium'
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>Средний</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Сбалансированные сценарии средней сложности
                      </p>
                    </div>
                    
                    <div 
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        settings.reelsComplexity === 'complex' 
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                      onClick={() => handleComplexityChange('complex')}
                    >
                      <span className={`font-medium text-center ${
                        settings.reelsComplexity === 'complex'
                          ? 'text-blue-700 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>Сложный</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                        Креативные сценарии с продвинутыми элементами
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Частота публикаций */}
              <div>
                <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-4">
                  Частота публикаций (в неделю)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {isInstagram && settings?.instagramFormats?.reels && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество Reels
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('reels', Math.max(0, (settings?.frequency?.reels || 0) - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={settings?.frequency?.reels || 0}
                          onChange={(e) => handleFrequencyChange('reels', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('reels', Math.min(7, (settings?.frequency?.reels || 0) + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {isInstagram && settings?.instagramFormats?.carousel && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество каруселей
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('carousel', Math.max(0, (settings?.frequency?.carousel || 0) - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={settings?.frequency?.carousel || 0}
                          onChange={(e) => handleFrequencyChange('carousel', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('carousel', Math.min(7, (settings?.frequency?.carousel || 0) + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {isInstagram && settings?.instagramFormats?.post && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество постов
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('post', Math.max(0, (settings?.frequency?.post || 0) - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={settings?.frequency?.post || 0}
                          onChange={(e) => handleFrequencyChange('post', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('post', Math.min(7, (settings?.frequency?.post || 0) + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {isTelegram && (
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Количество постов в Telegram
                      </label>
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={() => handleFrequencyChange('telegram', Math.max(0, (settings?.frequency?.telegram || 0) - 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-l-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="0"
                          max="7"
                          value={settings?.frequency?.telegram || 0}
                          onChange={(e) => handleFrequencyChange('telegram', parseInt(e.target.value) || 0)}
                          className="w-16 h-10 text-center border-t border-b border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-0 focus:border-blue-500 dark:bg-gray-700 dark:text-white text-lg font-medium"
                        />
                        <button 
                          onClick={() => handleFrequencyChange('telegram', Math.min(7, (settings?.frequency?.telegram || 0) + 1))}
                          className="w-10 h-10 flex items-center justify-center rounded-r-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Кнопки сохранения и отмены */}
              {hasChanges && (
                <div className="flex items-center justify-end space-x-3 pt-6 pb-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Отменить</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Сохранение...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Сохранить</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Не удалось загрузить настройки
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}; 